import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Calendar, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { confirmReschedule } from '../lib/rescheduleService';
import { getAvailableDates, isDateAvailable, getAvailableTimesForDate, organizeTimesByPeriod } from '../lib/availability';
import "react-day-picker/dist/style.css";

const RescheduleNewDate: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Get parameters from URL
  const email = searchParams.get('email') || '';
  const oldDate = searchParams.get('old_date') || '';
  const oldTime = searchParams.get('old_time') || '';
  const timestamp = searchParams.get('t') || '';

  const content = {
    en: {
      title: "Select New Date & Time",
      subtitle: "Choose your new appointment date and time from available slots",
      form: {
        date: "Select Date",
        time: "Select Time",
        submit: "Confirm Reschedule"
      },
      timeSlots: {
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening"
      },
      success: {
        title: "Appointment Rescheduled",
        message: "Your appointment has been successfully rescheduled."
      },
      error: {
        selectDate: "Please select a new date",
        selectTime: "Please select a new time",
        generic: "Something went wrong. Please try again."
      }
    },
    da: {
      title: "Vælg Ny Dato & Tid",
      subtitle: "Vælg din nye aftaledato og tid fra tilgængelige tider",
      form: {
        date: "Vælg Dato",
        time: "Vælg Tid",
        submit: "Bekræft Ombookning"
      },
      timeSlots: {
        morning: "Morgen",
        afternoon: "Eftermiddag",
        evening: "Aften"
      },
      success: {
        title: "Aftale Ombooked",
        message: "Din aftale er blevet ombooked."
      },
      error: {
        selectDate: "Vælg venligst en ny dato",
        selectTime: "Vælg venligst et nyt tidspunkt",
        generic: "Noget gik galt. Prøv venligst igen."
      }
    }
  };

  // Redirect if missing required parameters
  useEffect(() => {
    if (!email || !oldDate || !oldTime) {
      navigate('/reschedule-appointment');
      return;
    }
    
    // Reset all state when component mounts or parameters change (including timestamp)
    setSelectedDate(undefined);
    setSelectedTime('');
    setAvailableDates([]);
    setAvailableTimes([]);
    setError(null);
    setIsConfirmed(false);
    
    // Load available dates
    loadAvailableDates();
  }, [email, oldDate, oldTime, timestamp, navigate]);

  // Load available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      loadTimesForSelectedDate(selectedDate);
    }
  }, [selectedDate]);

  const loadAvailableDates = async () => {
    setIsLoading(true);
    try {
      const dates = await getAvailableDates();
      setAvailableDates(dates);
    } catch (err) {
      console.error('Error loading available dates:', err);
      setAvailableDates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTimesForSelectedDate = async (date: Date) => {
    setIsLoading(true);
    setSelectedTime('');
    try {
      const times = await getAvailableTimesForDate(date);
      setAvailableTimes(times);
    } catch (err) {
      console.error('Error loading times for date:', err);
      setAvailableTimes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!selectedDate) {
      setError(content[language as 'en' | 'da'].error.selectDate);
      setIsSubmitting(false);
      return;
    }

    if (!selectedTime) {
      setError(content[language as 'en' | 'da'].error.selectTime);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await confirmReschedule({
        email,
        old_date: oldDate,
        old_time: oldTime,
        new_date: format(selectedDate, 'yyyy-MM-dd'),
        new_time: selectedTime
      });

      if (result.success) {
        setIsConfirmed(true);
        // Redirect to success page after 3 seconds
        setTimeout(() => {
          navigate('/success', { 
            state: { 
              type: 'reschedule',
              appointment: {
                email_address: email,
                old_date: oldDate,
                old_time: oldTime,
                new_date: format(selectedDate, 'yyyy-MM-dd'),
                new_time: selectedTime,
                status: 'rescheduled'
              }
            } 
          });
        }, 3000);
      } else {
        setError(result.error || content[language as 'en' | 'da'].error.generic);
      }
    } catch (err) {
      console.error('Error confirming reschedule:', err);
      setError(content[language as 'en' | 'da'].error.generic);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTimeSlots = () => {
    if (availableTimes.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">
            {language === 'da' 
              ? 'Ingen tilgængelige tider for den valgte dato' 
              : 'No available times for the selected date'}
          </p>
        </div>
      );
    }

    const { morning, afternoon, evening } = organizeTimesByPeriod(availableTimes);
    const periods = [
      { key: 'morning', times: morning, label: content[language as keyof typeof content].timeSlots.morning },
      { key: 'afternoon', times: afternoon, label: content[language as keyof typeof content].timeSlots.afternoon },
      { key: 'evening', times: evening, label: content[language as keyof typeof content].timeSlots.evening }
    ].filter(period => period.times.length > 0);

    if (periods.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">
            {language === 'da' 
              ? 'Ingen tilgængelige tider for den valgte dato' 
              : 'No available times for the selected date'}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {periods.map(({ key, times, label }) => (
          <div key={key}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {label}
            </h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {times.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => setSelectedTime(time)}
                  className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                    selectedTime === time
                      ? 'bg-[#A68B7C] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isConfirmed) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1
                className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {content[language as 'en' | 'da'].success.title}
              </h1>
              
              <p className="text-lg text-gray-300 mb-8">
                {content[language as 'en' | 'da'].success.message}
              </p>

              <p className="text-sm text-gray-400">
                Redirecting to homepage in 3 seconds...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-serif font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              {content[language as keyof typeof content].title}
            </h1>
            <p className="text-gray-600">
              {content[language as keyof typeof content].subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Date Selection - Exact same as BookingForm */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#A68B7C]" />
                <h3 className="text-lg font-semibold">{content[language as keyof typeof content].form.date}</h3>
              </div>
                            <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={language === 'da' ? da : undefined}
                disabled={(date) => {
                  // Disable dates before today (midnight)
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Set to midnight of today
                  if (date < today) return true;
                  // Disable dates not in available dates
                  return !isDateAvailable(date, availableDates);
                }}
                className="mx-auto"
                modifiersStyles={{
                  selected: { backgroundColor: '#A68B7C', color: 'white' }
                }}
              />

            </div>

            {/* Time Selection */}
            {selectedDate && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-[#A68B7C]" />
                  <h3 className="text-lg font-semibold">{content[language as keyof typeof content].form.time}</h3>
                </div>
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-gray-600">
                        {language === 'da' ? 'Indlæser tider...' : 'Loading times...'}
                      </span>
                    </div>
                  </div>
                ) : (
                  renderTimeSlots()
                )}
              </div>
            )}

            {/* Submit Button */}
            {selectedTime && (
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting || !selectedDate || !selectedTime}
                  className="px-8 py-3 bg-[#A68B7C] text-white font-medium rounded-lg hover:bg-[#95796A] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    content[language as keyof typeof content].form.submit
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default RescheduleNewDate;